import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { Box } from "@mui/material";

const ELLDialog = ({ open, onClose, reset }) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState([]);
    const { control } = useForm();

    const steps = [
        {
            value: "not-native",
            label: t("Home Language — One of these must be true:"),
            items: [
                t("My child’s first language is not English."),
                t("My child was not born in the United States, or grew up in a home where another language is spoken."),
                t("My child is Native American or Alaska Native and grew up where another language is spoken."),
                t("My child is migratory, and their first language is not English."),
            ],
        },
        {
            value: "difficulty-speaking",
            label: t("Difficulty with English — All of these must be true:"),
            items: [
                t("My child learned another language before English."),
                t("My child mostly speaks another language at home."),
                t("My child is more comfortable speaking another language with friends or family."),
            ],
        },
        {
            value: "current_ell",
            label: t("Current ESL/ELL Program — Provide one of these as proof:"),
            items: [
                t("Test results showing English proficiency (e.g., ACCESS test)."),
                t("A notice from school about ESL or bilingual program."),
                t("Report cards or progress reports showing ESL/ELL services."),
            ],
        },
    ];

    const handleConfirm = () => {
        if (selected.length > 0) {
            onClose();
        }
    };

    const handleCancel = () => {
        setSelected([]);
        reset("isELL");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={() => {}}
            disableEscapeKeyDown
            maxWidth="sm"
            TransitionProps={{ onEntering: () => setSelected([]) }}
        >
            <DialogTitle>{t("Weighted Lottery for English Language Learners (ELLs)")}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" gutterBottom>
                    {t(
                        "Passaic and Paterson ASCS use a weighted lottery to make sure English Language Learners have a fair chance."
                    )}
                    {" "}
                    {t(
                        "If your child qualifies, they will receive two tickets in the lottery instead of one."
                    )}
                </Typography>

                <Controller
                    name="languageOptions"
                    control={control}
                    defaultValue={[]}
                    rules={{
                        validate: (value) =>
                            value?.length > 0 || t("Please select at least one option."),
                    }}
                    render={({ field, fieldState }) => (
                        <FormGroup>
                            {steps.map((step) => (
                                <Box key={step.value} sx={{ mb: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.value?.includes(step.value) || false}
                                                onChange={(e) => {
                                                    let newSelected;
                                                    if (e.target.checked) {
                                                        newSelected = [...(field.value || []), step.value];
                                                    } else {
                                                        newSelected = (field.value || []).filter((v) => v !== step.value);
                                                    }
                                                    field.onChange(newSelected);
                                                    setSelected(newSelected);
                                                }}
                                            />
                                        }
                                        label={<Typography variant="body1" fontWeight={500}>{step.label}</Typography>}
                                    />
                                    <Box sx={{ pl: 4, mt: 0 }}>
                                        {step.items.map((item, idx) => (
                                            <Typography key={idx} variant="body2" >
                                                • {item}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                            {fieldState.error && (
                                <p style={{ color: "red", fontSize: "0.875rem" }}>
                                    {fieldState.error.message}
                                </p>
                            )}
                        </FormGroup>
                    )}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>{t("Child Not Eligible")}</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={selected.length === 0}
                >
                    {t("Confirm")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ELLDialog;
